/**
 * NotInMeetingPage - Shown when app is accessed outside of a Zoom meeting
 *
 * This page displays when:
 * 1. After OAuth installation from Zoom Marketplace (redirected with ?install=success)
 * 2. When opening the app from Zoom client's main page (not in a meeting)
 * 3. When accessing the app URL directly in a browser
 *
 * Shows friendly instructions on how to properly use the app.
 */

import '../styles/onboarding.css';

interface NotInMeetingPageProps {
  isInstallSuccess?: boolean;
}

// Detect if running inside Zoom App
const isInZoomApp = navigator.userAgent.includes('ZoomApps');

export function NotInMeetingPage({ isInstallSuccess }: NotInMeetingPageProps): JSX.Element {
  const handleOpenZoom = () => {
    // Try to open Zoom client (only works in browser, not inside Zoom app)
    window.location.href = 'zoommtg://zoom.us/start';
  };

  return (
    <div className="therapist-onboarding">
      <div className="therapist-onboarding-content">
        {/* Logo */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <img
            src="/assets/logos/transparent-eye-no-borders.png"
            alt="EMDR Sync"
            style={{ width: '100px', height: 'auto', margin: '0 auto' }}
          />
        </div>

        {isInstallSuccess ? (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg style={{ width: '32px', height: '32px', color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 style={{ fontSize: '1.75rem', marginBottom: '12px', textAlign: 'center', fontWeight: 700, color: '#1F2937' }}>
              Installation Complete!
            </h1>
            <p style={{ marginBottom: '24px', textAlign: 'center', color: '#6B7280', lineHeight: '1.6' }}>
              EMDR Sync has been successfully added to your Zoom account.
            </p>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: '1.75rem', marginBottom: '12px', textAlign: 'center', fontWeight: 700, color: '#1F2937' }}>
              Open in a Meeting
            </h1>
            <p style={{ marginBottom: '24px', textAlign: 'center', color: '#6B7280', lineHeight: '1.6' }}>
              EMDR Sync works inside Zoom meetings to deliver synchronized bilateral stimulation therapy.
            </p>
          </>
        )}

        <div style={{
          background: '#EFF6FF',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'left',
          marginBottom: '24px',
        }}>
          <p style={{ fontWeight: 600, color: '#1E40AF', marginBottom: '16px', fontSize: '0.9375rem' }}>
            To use EMDR Sync:
          </p>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: '#1E40AF' }}>
              <span style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                color: 'white',
                fontSize: '0.8125rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>1</span>
              <span style={{ paddingTop: '2px' }}>Start or join a Zoom meeting</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: '#1E40AF' }}>
              <span style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                color: 'white',
                fontSize: '0.8125rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>2</span>
              <span style={{ paddingTop: '2px' }}>Click the <strong>Apps</strong> button in the toolbar</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: '#1E40AF' }}>
              <span style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                color: 'white',
                fontSize: '0.8125rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>3</span>
              <span style={{ paddingTop: '2px' }}>Select <strong>EMDR Sync</strong> from your apps</span>
            </li>
          </ol>
        </div>

        {!isInZoomApp && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleOpenZoom}
              className="zoom-auth-button"
            >
              Open Zoom
            </button>
            <p style={{ marginTop: '12px', fontSize: '0.8125rem', color: '#9CA3AF' }}>
              Opens the Zoom application on your device
            </p>
          </div>
        )}
      </div>
    </div>
  );
}